import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Wrapper } from './style';
import OrderItems from './lib/order-items';
import Products from './lib/products';
import { MakeApiCall } from "../../../../apis";
import { ConvertParams } from "../../../../config";
import { message, Spin, Checkbox, Dropdown, Button, Select, Input, InputNumber, Modal, Divider } from "antd";
import { SettingOutlined, FilterOutlined, CloseCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { exportToExcel } from "../../../../utils/excelExport";

// ─── Operator display labels ──────────────────────────────────────────────────

const OPERATOR_LABELS = {
    gte:      '≥  Greater than or equal to',
    lte:      '≤  Less than or equal to',
    eq:       '=  Equals',
    contains: 'Contains',
};

const OPERATOR_SHORT = {
    gte: '≥', lte: '≤', eq: '=', contains: 'contains',
};

// ─── FilterModal component ────────────────────────────────────────────
/**
 * Trigger button + antd Modal.
 * filterControls: {
 *   filterableColumns, activeFilters,
 *   addFilter, updateFilter, removeFilter, clearFilters
 * }
 *
 * Rules enforced here:
 *   - Only ONE filter per column is allowed.
 *   - Selecting a column that already has a filter switches to EDIT mode:
 *     the existing operator & value are pre-loaded and the button says
 *     "Update Filter". On submit the filter is updated in-place.
 */
const FilterPanel = ({ filterControls }) => {
    const [open, setOpen] = useState(false);

    // Form draft
    const [newCol, setNewCol] = useState(null);
    const [newOp,  setNewOp]  = useState(null);
    const [newVal, setNewVal] = useState('');

    if (!filterControls) return null;

    const {
        filterableColumns,
        activeFilters,
        addFilter,
        updateFilter,
        removeFilter,
        clearFilters,
    } = filterControls;

    // Is the currently-selected column already filtered?
    const existingFilter = activeFilters.find(f => f.column === newCol);
    const isEditMode     = !!existingFilter;
    const colDef         = filterableColumns.find(c => c.key === newCol);

    const operatorOptions = colDef?.type === 'numeric'
        ? [
            { value: 'gte', label: '≥  Greater than or equal to' },
            { value: 'lte', label: '≤  Less than or equal to'    },
            { value: 'eq',  label: '=  Equals'                   },
        ]
        : [
            { value: 'contains', label: 'Contains'       },
            { value: 'eq',       label: 'Equals (exact)' },
        ];

    // When user picks a column:
    //   - If that column already has a filter, pre-load its values (edit mode)
    //   - Otherwise reset the form (add mode)
    const handleColChange = val => {
        const existing = activeFilters.find(f => f.column === val);
        setNewCol(val);
        if (existing) {
            setNewOp(existing.operator);
            setNewVal(String(existing.value));
        } else {
            setNewOp(null);
            setNewVal('');
        }
    };

    const handleSubmit = () => {
        if (!newCol || !newOp || newVal === '' || newVal === null) return;

        const value = colDef?.type === 'numeric' ? parseFloat(newVal) : newVal;

        if (isEditMode) {
            // Update the existing filter in-place
            updateFilter(existingFilter.id, { operator: newOp, value });
        } else {
            addFilter({
                id:          `${newCol}_${Date.now()}`,
                column:      newCol,
                columnLabel: colDef?.label,
                type:        colDef?.type,
                operator:    newOp,
                value,
            });
        }

        // After submit: reset to a clean "add" state
        setNewCol(null);
        setNewOp(null);
        setNewVal('');
    };

    const handleClose = () => {
        setOpen(false);
        setNewCol(null);
        setNewOp(null);
        setNewVal('');
    };

    const canSubmit = newCol && newOp && newVal !== '' && newVal !== null;

    // Columns already used — shown with a dot indicator in the selector
    const usedColumnKeys = new Set(activeFilters.map(f => f.column));

    return (
        <>
            {/* ── Trigger button ── */}
            <button
                className={`btn fs-7 fw-bold d-flex align-items-center gap-2 ${activeFilters.length > 0 ? 'btn-danger' : 'btn-dark'}`}
                onClick={() => setOpen(true)}
            >
                <FilterOutlined />
                Filters
                {activeFilters.length > 0 && (
                    <span
                        className="badge rounded-pill bg-white text-danger"
                        style={{ fontSize: 10, minWidth: 18, padding: '1px 6px', lineHeight: '14px' }}
                    >
                        {activeFilters.length}
                    </span>
                )}
            </button>

            {/* ── Modal ── */}
            <Modal
                open={open}
                onCancel={handleClose}
                title={
                    <div className="d-flex align-items-center gap-2">
                        <FilterOutlined style={{ color: '#dc3545' }} />
                        <span className="fw-bold">Column Filters</span>
                        {activeFilters.length > 0 && (
                            <span className="badge bg-danger ms-1" style={{ fontSize: 11 }}>
                                {activeFilters.length} active
                            </span>
                        )}
                    </div>
                }
                footer={
                    <div className="d-flex align-items-center justify-content-between">
                        <span style={{ fontSize: 11, color: '#aaa' }}>
                            Filters are applied client-side — no API call is made.
                        </span>
                        <div className="d-flex gap-2">
                            {activeFilters.length > 0 && (
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={clearFilters}
                                >
                                    Clear all filters
                                </button>
                            )}
                            <button className="btn btn-sm btn-dark" onClick={handleClose}>
                                Done
                            </button>
                        </div>
                    </div>
                }
                width={560}
                styles={{ body: { padding: '8px 0', maxHeight: '70vh', overflowY: 'auto' } }}
                destroyOnClose={false}
            >

                {/* ── Applied filters list ── */}
                {activeFilters.length > 0 && (
                    <div style={{ padding: '0 24px 16px' }}>
                        <div
                            style={{
                                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                                letterSpacing: '0.5px', color: '#999', marginBottom: 10,
                            }}
                        >
                            Applied Filters
                        </div>
                        <div className="d-flex flex-column gap-2">
                            {activeFilters.map(f => {
                                const isBeingEdited = newCol === f.column;
                                return (
                                    <div
                                        key={f.id}
                                        className="d-flex align-items-center justify-content-between rounded-2 px-3 py-2"
                                        style={{
                                            background:  isBeingEdited ? '#fff3cd' : '#fff4f4',
                                            border:      isBeingEdited ? '1px solid #ffc107' : '1px solid #ffd6d6',
                                            transition:  'background 0.2s, border-color 0.2s',
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="fw-semibold" style={{ fontSize: 13, color: '#1e1e2d' }}>
                                                {f.columnLabel}
                                            </span>
                                            <span style={{
                                                fontSize: 11, background: '#e9ecef', color: '#495057',
                                                padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                            }}>
                                                {OPERATOR_SHORT[f.operator]}
                                            </span>
                                            <span className="fw-bold" style={{ fontSize: 13, color: '#dc3545' }}>
                                                {String(f.value)}
                                            </span>
                                            {isBeingEdited && (
                                                <span
                                                    style={{
                                                        fontSize: 10, background: '#ffc107', color: '#000',
                                                        padding: '1px 7px', borderRadius: 3, fontWeight: 600,
                                                    }}
                                                >
                                                    editing
                                                </span>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {/* Edit button */}
                                            <button
                                                className="btn p-0"
                                                style={{ color: isBeingEdited ? '#ffc107' : '#adb5bd', lineHeight: 1, fontSize: 13 }}
                                                onClick={() => handleColChange(f.column)}
                                                title="Edit this filter"
                                            >
                                                <i className="bi bi-pencil" />
                                            </button>
                                            {/* Delete button */}
                                            <button
                                                className="btn p-0"
                                                style={{ color: '#ccc', lineHeight: 1 }}
                                                onClick={() => {
                                                    removeFilter(f.id);
                                                    // If we were editing this filter, clear the form
                                                    if (isBeingEdited) {
                                                        setNewCol(null);
                                                        setNewOp(null);
                                                        setNewVal('');
                                                    }
                                                }}
                                                title="Remove this filter"
                                            >
                                                <DeleteOutlined style={{ fontSize: 14 }} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Divider style={{ margin: '16px 0 0' }} />
                    </div>
                )}

                {/* ── Add / Edit filter form ── */}
                <div style={{ padding: '16px 24px 8px' }}>
                    <div
                        style={{
                            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: '0.5px', marginBottom: 14,
                            color: isEditMode ? '#856404' : '#999',
                        }}
                    >
                        {isEditMode
                            ? `✎  Editing filter: ${colDef?.label}`
                            : activeFilters.length > 0 ? 'Add Another Filter' : 'Add Filter'}
                    </div>

                    {/* Step 1 — Column */}
                    <div className="mb-3">
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 6 }}>
                            1. Select Column
                        </div>
                        <Select
                            placeholder="Choose a column to filter..."
                            style={{ width: '100%' }}
                            size="large"
                            value={newCol}
                            onChange={handleColChange}
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            options={filterableColumns.map(c => ({
                                value: c.key,
                                label: c.label,
                                // Columns already filtered are shown with a pencil hint
                                disabled: false,
                            }))}
                            optionRender={option => (
                                <div className="d-flex align-items-center justify-content-between">
                                    <span>{option.label}</span>
                                    {usedColumnKeys.has(option.value) && (
                                        <span
                                            style={{
                                                fontSize: 10, background: option.value === newCol ? '#ffc107' : '#e9ecef',
                                                color: option.value === newCol ? '#000' : '#888',
                                                padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                                            }}
                                        >
                                            {option.value === newCol ? 'editing' : 'filtered'}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    {/* Step 2 — Condition */}
                    {newCol && (
                        <div className="mb-3">
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 6 }}>
                                2. Select Condition
                                <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400, marginLeft: 6 }}>
                                    ({colDef?.type === 'numeric' ? 'numeric' : 'text'})
                                </span>
                            </div>
                            <Select
                                placeholder="Choose a condition..."
                                style={{ width: '100%' }}
                                size="large"
                                value={newOp}
                                onChange={val => { setNewOp(val); if (!isEditMode) setNewVal(''); }}
                                options={operatorOptions}
                            />
                        </div>
                    )}

                    {/* Step 3 — Value */}
                    {newCol && newOp && (
                        <div className="mb-4">
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 6 }}>
                                3. Enter Value
                            </div>
                            {colDef?.type === 'numeric' ? (
                                <InputNumber
                                    placeholder="Enter a number..."
                                    style={{ width: '100%' }}
                                    size="large"
                                    value={newVal === '' ? null : newVal}
                                    onChange={val => setNewVal(val ?? '')}
                                    controls={false}
                                />
                            ) : (
                                <Input
                                    placeholder="Enter text..."
                                    size="large"
                                    value={newVal}
                                    onChange={e => setNewVal(e.target.value)}
                                    onPressEnter={() => { if (canSubmit) handleSubmit(); }}
                                />
                            )}
                        </div>
                    )}

                    {/* Submit button — label changes between Add / Update */}
                    <button
                        className={`btn fw-bold w-100 d-flex align-items-center justify-content-center gap-2 ${isEditMode ? 'btn-warning text-dark' : 'btn-danger'}`}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        style={{ opacity: canSubmit ? 1 : 0.45, padding: '9px 0' }}
                    >
                        {isEditMode
                            ? <><i className="bi bi-pencil-fill" style={{ fontSize: 12 }} /> Update Filter</>
                            : <><PlusOutlined /> Add Filter</>
                        }
                    </button>

                    {/* Cancel edit link */}
                    {isEditMode && (
                        <button
                            className="btn btn-sm btn-link text-muted w-100 mt-2"
                            style={{ fontSize: 12 }}
                            onClick={() => { setNewCol(null); setNewOp(null); setNewVal(''); }}
                        >
                            Cancel edit
                        </button>
                    )}
                </div>

            </Modal>
        </>
    );
};

// ─── ItemMaster component ─────────────────────────────────────────────────────

const ItemMaster = ({ userData, selectedMarketplace, selectedCurrencySign, selectedTileData }) => {
    const [activeView, setActiveView]     = useState('products');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef                     = useRef();
    const [loading, setLoading]           = useState(false);

    // Column controls lifted from Products (for Customize Columns button)
    const [columnControls, setColumnControls] = useState(null);
    // { toggleableColumns, visibleColumns, handleColumnToggle }

    // Filter controls lifted from Products (for FilterPanel button)
    const [filterControls, setFilterControls] = useState(null);
    // { filterableColumns, activeFilters, addFilter, removeFilter, clearFilters }

    // ── Export (unchanged) ───────────────────────────────────────────────────
    const getProductList = async (filter, forExport = false) => {
        setLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/tiles/get-product-analytics-data${ConvertParams(filter)}`,
                "get",
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                const records = response?.data?.records?.summaryData || [];
                const labels  = response?.data?.records?.label       || {};
                if (forExport) exportToExcel(records, labels, "products.xlsx");
            } else {
                message.warning(response?.message);
            }
        } catch {
            message.error("Something went wrong while exporting.");
        } finally {
            setLoading(false);
        }
    };

    // ── View switcher click-outside ──────────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDropdown = () => setDropdownOpen(prev => !prev);
    const selectView     = view => { setActiveView(view); setDropdownOpen(false); };

    const handleExport = () => {
        const filter = {
            marketplace_id: selectedMarketplace?.id,
            start_date:     selectedTileData?.start_date,
            end_date:       selectedTileData?.end_date,
            no_pagination:  true,
        };
        getProductList(filter, true);
    };

    // ── Stable callback for Products → setFilterControls ────────────────────
    const handleFiltersReady = useCallback(controls => {
        setFilterControls(controls);
    }, []);

    return (
        <Wrapper>
            <div className="card card-xxl-stretch mb-5 mb-xl-8 mt-4 border-0 shadow-sm rounded-4">

                {/* ── Header ── */}
                <div className="card-header bg-transparent pb-0 px-5 d-flex flex-wrap align-items-center gap-2">

                    {/* View Switcher Dropdown */}
                    <div className="dropdown position-relative" ref={dropdownRef}>
                        <button
                            className="btn btn-danger btn-lg fw-semibold px-10 py-2 rounded-1 shadow-sm border d-flex align-items-center"
                            onClick={toggleDropdown}
                        >
                            {activeView === 'products'
                                ? <i className="bi bi-box-seam me-2 fs-4"></i>
                                : <i className="bi bi-cart3 me-2 fs-4"></i>}
                            {activeView === 'products' ? 'Products' : 'Order Items'}
                            <i className="ms-2 bi bi-caret-down-fill fs-7"></i>
                        </button>
                        <ul
                            className={`dropdown-menu ${dropdownOpen ? "show" : ""}`}
                            style={{ width: "100%", minWidth: "unset", boxSizing: "border-box" }}
                        >
                            <li>
                                <button
                                    className={`dropdown-item ${activeView === 'products' ? 'active' : ''}`}
                                    onClick={() => selectView('products')}
                                    style={{
                                        backgroundColor: activeView === 'products' ? '#dc3545' : '#f8f9fa',
                                        color:           activeView === 'products' ? '#fff'    : '#000',
                                        fontWeight: 500, padding: "10px 15px", borderRadius: 4,
                                        marginBottom: 5, border: "1px solid #dee2e6", textAlign: "center",
                                    }}
                                >
                                    <i className="bi bi-box-seam me-2 fs-4"></i>Products
                                </button>
                            </li>
                            <li>
                                <button
                                    className={`dropdown-item ${activeView === 'orderItems' ? 'active' : ''}`}
                                    onClick={() => selectView('orderItems')}
                                    style={{
                                        backgroundColor: activeView === 'orderItems' ? '#dc3545' : '#f8f9fa',
                                        color:           activeView === 'orderItems' ? '#fff'    : '#000',
                                        fontWeight: 500, padding: "10px 15px", borderRadius: 4,
                                        border: "1px solid #dee2e6", textAlign: "center",
                                    }}
                                >
                                    <i className="bi bi-cart3 me-2 fs-4"></i>Order Items
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* ── Products-only action buttons ── */}
                    {activeView === 'products' && (
                        <div className="d-flex align-items-center ms-auto gap-2">

                            {/* Filter button + panel */}
                            <FilterPanel filterControls={filterControls} />

                            {/* Customize Columns */}
                            {columnControls && (
                                <Dropdown
                                    trigger={['click']}
                                    placement="bottomRight"
                                    overlay={
                                        <div
                                            className="card shadow p-4"
                                            style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '220px' }}
                                        >
                                            <div className="fw-bold mb-3 border-bottom pb-2">Display Columns</div>
                                            <Checkbox.Group
                                                options={columnControls.toggleableColumns.map(c => ({
                                                    label: c.title,
                                                    value: c.key,
                                                }))}
                                                value={columnControls.visibleColumns.filter(
                                                    key => key !== 'index' && key !== 'productDetails' && key !== 'details'
                                                )}
                                                onChange={columnControls.handleColumnToggle}
                                                className="d-flex flex-column gap-2"
                                            />
                                        </div>
                                    }
                                >
                                    <Button
                                        icon={<SettingOutlined />}
                                        className="btn btn-dark fs-7 fw-bold d-flex align-items-center"
                                        style={{ height: 'auto', border: 'none' }}
                                    >
                                        Customize Columns
                                    </Button>
                                </Dropdown>
                            )}

                            {/* Export */}
                            <button
                                className="btn btn-dark fs-7 fw-bold d-flex align-items-center"
                                onClick={handleExport}
                                disabled={loading}
                            >
                                {loading ? (
                                    <><Spin size="small" className="me-2" /> Exporting...</>
                                ) : (
                                    <>Export <i className="ms-1 bi bi-upload fs-4" /></>
                                )}
                            </button>

                        </div>
                    )}
                </div>

                {/* ── Body ── */}
                <div className="card-body pt-4 px-5">
                    {activeView === 'products' && (
                        <Products
                            selectedTileData={selectedTileData}
                            userData={userData}
                            selectedCurrencySign={selectedCurrencySign}
                            selectedMarketplace={selectedMarketplace}
                            onColumnsReady={setColumnControls}
                            onFiltersReady={handleFiltersReady}
                        />
                    )}
                    {activeView === 'orderItems' && (
                        <OrderItems
                            selectedTileData={selectedTileData}
                            userData={userData}
                            selectedCurrencySign={selectedCurrencySign}
                            selectedMarketplace={selectedMarketplace}
                        />
                    )}
                </div>

            </div>
        </Wrapper>
    );
};

export default ItemMaster;